import { StandardEvents } from '@shopify/events';

/**
 * `<card-size-selector>` turns a click on a size button into an add-to-cart.
 *
 * The element wraps a minimal `<product-form-component>` (see
 * snippets/product-card-sizes-markup.liquid, which product-card-sizes.liquid renders
 * alongside the overlay's styles). On click it sets the hidden variant input and submits
 * the form; Horizon's product-form-component handles the AJAX add and (if the theme's
 * global auto-open is enabled) the cart-drawer auto-opens.
 *
 * It is only rendered when the global `show_sizes_on_hover` setting is enabled,
 * so its mere presence means clicks should add to cart.
 *
 * When `data-open-cart="true"` (the "abrir carrito al agregar desde la talla"
 * setting), it opens the cart drawer once the add resolves — independent of the
 * theme's global auto-open setting, so only size adds trigger it.
 */
// Comfortable headroom above dev-store measured add latency (~1.2-1.4s). This watchdog only
// exists to recover a chip that somehow got stuck in `pending` through a path we didn't
// anticipate; it must never fire during a normal add.
const PENDING_WATCHDOG_MS = 5000;

// How long the `added` / `error` confirmation stays on the chip before it reverts to rest.
const RESOLVED_STATE_RESET_MS = 1200;

// Shown when an add fails without a server-provided reason -- the network-error catch in
// product-form.js, or the synchronous no-promise branch below where nothing was ever sent.
const GENERIC_ADD_ERROR_MESSAGE = 'No pudimos agregar esta talla al carrito. Intentá nuevamente.';

// Upper bound on how many successive colour-change fetches a size click will wait out (see
// the `#pendingVariantChange` wait loop in `#handleClick`) before giving up on the add
// entirely. Horizon's variant-picker aborts an in-flight section-rendering fetch and starts a
// new one the moment a newer swatch click lands, rejecting the promise this element was
// waiting on -- so a fast enough shopper could in principle keep superseding the wait
// indefinitely. This caps it instead of leaving the chip pending forever. Five consecutive
// colour changes inside the span of one size click is already far beyond plausible human
// clicking speed.
const MAX_VARIANT_CHANGE_WAIT_PASSES = 5;

// How long an announcement stays in the live region before being cleared. `aria-live` only
// announces on a text change, so adding the same size twice in a row (identical message) would
// otherwise announce nothing the second time -- see assets/product-form.js's
// `#setLiveRegionText` / `#clearLiveRegionText` for the same pairing. Long enough that a screen
// reader has already spoken the message, short enough that it's empty again well before a
// plausible next add.
const ANNOUNCEMENT_CLEAR_MS = 1000;

class CardSizeSelector extends HTMLElement {
  /**
   * `wasAriaDisabled` is the `aria-disabled` attribute value (or `null` if absent) the chip had
   * at rest, before this state cycle set it to `"true"` for `pending`. Carried across the whole
   * cycle (pending -> added/error -> reset) the same way the old `wasDisabled` boolean was, so
   * clearing the state restores the chip to whatever it looked like before -- see `#markState`.
   * @type {Map<HTMLButtonElement, { timer: number, wasAriaDisabled: string | null }>}
   */
  #resetTimers = new Map();

  /**
   * Each chip's current click "epoch": a counter bumped every time that chip is clicked.
   * `settle`'s async resolution (below) and the reset timer scheduled in `#markState` both
   * capture the epoch that was active when they started and compare it against this map's
   * live value before writing to the chip. A slow `/cart/add.js` can resolve after the
   * pending watchdog already reset the chip AND after the shopper re-clicked it -- without
   * this guard that late resolution would paint over the newer click's state and cancel its
   * in-flight watchdog. A WeakMap so a chip node wouldn't pin memory if it were ever removed
   * (see the `!button.isConnected` check in `#markState`) -- not just defensive: a colour
   * swatch change re-renders this overlay (see product-card.js's `updateSizeOverlay`), and
   * because that `morph` runs with `childrenOnly: true`, `<card-size-selector>` itself
   * survives every swap while its chip `<button>` nodes are reused in place and surplus ones
   * removed. So both the reuse case (epoch/timer state carried across a colour change on a
   * node that now means something else -- see `#isCurrentClick` in `#handleClick`) and the
   * removal case (`!button.isConnected`) are exercised in normal use, not hypothetical.
   * @type {WeakMap<HTMLButtonElement, number>}
   */
  #epochs = new WeakMap();

  /**
   * Timer id for the pending live-region clear scheduled by `#announce`, or `null` when none is
   * pending. Cancelled and rescheduled on every announcement so a fast second add doesn't wipe
   * out a fresh message before it's had time to be read.
   * @type {ReturnType<typeof setTimeout> | null}
   */
  #announceClearTimer = null;

  /**
   * The promise from a productSelect event currently in flight for this card -- the same
   * promise product-form.js's `#onProductSelect` holds as its own private `#pendingVariantChange`
   * and that puts THIS card's product-form-component into its variant-change window (see
   * product-form.js's `handleSubmit`, the `#variantChangeInProgress` branch around L327-331).
   * While that promise is pending, `handleSubmit` doesn't submit -- it queues the add instead,
   * and the queued item captures the VARIANT PICKER's selection (the colour swatch), not
   * whichever chip was clicked here. A size chip clicked during that window would therefore
   * silently commit the swatch's colour once the queue drains, with no error and no
   * announcement. `#variantChangeInProgress` itself is private to product-form.js, so this
   * element tracks the same in-flight promise independently from the same event (see
   * `#onProductSelect` below) and `#handleClick` holds the submit on it before ever calling
   * `form.requestSubmit()`.
   * @type {Promise<unknown> | null}
   */
  #pendingVariantChange = null;

  /**
   * The enclosing `<product-card>`, resolved once in `connectedCallback` and reused for
   * `removeEventListener` in `disconnectedCallback`. Not recomputed via `this.closest(...)` at
   * disconnect time: by the time `disconnectedCallback` runs, this node has already been
   * detached (`parentNode` is null per the node-removal algorithm), so `closest` would find
   * nothing there and the listener registered below would leak on the real product-card forever.
   * @type {Element | null}
   */
  #productCard = null;

  /**
   * Tracks this card's in-flight colour change (see `#pendingVariantChange` above) so
   * `#handleClick` can hold a size add until it settles. Bound to the enclosing `<product-card>`
   * in `connectedCallback`, mirroring product-form.js's own `#onProductSelect` registration
   * (`this.closest('.shopify-section, dialog, product-card')`) -- narrowed to `product-card`
   * here because this element only ever lives inside one (see the class doc comment). Binding on
   * `document` instead would also pick up every OTHER card's colour changes on the page, which
   * is not what "in flight" should mean for this card's own chips.
   * @param {any} event
   */
  #onProductSelect = (event) => {
    const promise = event?.promise;
    if (!promise) return;

    this.#pendingVariantChange = promise;
    const clear = () => {
      // Only clear if this is still the promise being tracked -- a second colour change can
      // start (and get captured here) before the first one's settlement callback runs, and
      // that later promise must win; otherwise this clear would wipe out tracking for a change
      // that's still genuinely in flight.
      if (this.#pendingVariantChange === promise) this.#pendingVariantChange = null;
    };
    promise.then(clear, clear);
  };

  connectedCallback() {
    this.addEventListener('click', this.#handleClick);

    this.#productCard = this.closest('product-card');
    this.#productCard?.addEventListener(StandardEvents.productSelect, this.#onProductSelect);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);

    this.#productCard?.removeEventListener(StandardEvents.productSelect, this.#onProductSelect);
    this.#productCard = null;

    for (const entry of this.#resetTimers.values()) clearTimeout(entry.timer);
    this.#resetTimers.clear();

    if (this.#announceClearTimer !== null) clearTimeout(this.#announceClearTimer);
    this.#announceClearTimer = null;
  }

  /**
   * @param {Event} event
   */
  #handleClick = async (event) => {
    const button =
      event.target instanceof Element ? event.target.closest('[data-variant-id]') : null;
    // `button.disabled` still gates sold-out chips (a real `disabled` attribute rendered in the
    // markup, untouched by this feature). `pending` re-entry is guarded separately via
    // `data-state`: pending no longer sets the real `disabled` property (see `#markState`) so a
    // keyboard shopper's Enter press doesn't blur the chip and hide the overlay that's only
    // revealed by hover/`:focus-within` in the first place.
    if (!(button instanceof HTMLButtonElement) || button.disabled || button.dataset.state === 'pending') return;

    // Never let the click navigate the underlying card link.
    event.preventDefault();
    event.stopPropagation();

    const form = this.querySelector('form');
    const variantInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[name="id"]')
    );
    const pfc = this.querySelector('product-form-component');
    if (!form || !variantInput || !pfc) return;

    const variantId = button.getAttribute('data-variant-id');
    if (!variantId) return;

    variantInput.value = variantId;

    // Bump this chip's epoch before anything else. `settle` below captures it by closure, and
    // its async branches compare against this map's live value before writing to the chip, so
    // a superseded click's late resolution can never win over a click that came after it.
    const epoch = (this.#epochs.get(button) ?? 0) + 1;
    this.#epochs.set(button, epoch);

    // Cada respuesta se ata al chip que la origino: con dos clicks rapidos en tallas
    // distintas hay dos listeners en vuelo, asi que sin esto la confirmacion caeria
    // siempre en el ultimo chip clickeado.
    this.#markState(button, 'pending');

    // Hold the submit until any colour change already in flight on this card settles. Read
    // right here, before doing anything else with it, rather than captured earlier in the
    // handler -- nothing above this point is async, so `this.#pendingVariantChange` is exactly
    // as fresh here as it would be at the top.
    let pendingVariantChange = this.#pendingVariantChange;
    if (pendingVariantChange) {
      let passes = 0;
      let bailed = false;

      // A single await isn't enough: Horizon's variant-picker aborts an in-flight
      // section-rendering fetch and rejects its promise the instant a NEWER swatch click
      // lands, so a colour change B that starts while this click is already waiting on
      // colour change A resolves A's promise (via rejection) without ever having morphed
      // anything -- the chip's `data-variant-id` would still read as a match, `#handleClick`
      // would restore the input and submit, and `product-form.js` would still be sitting in
      // B's variant-change window (its `finally` only clears `#variantChangeInProgress` when
      // ITS OWN generation is still current), so the submit gets queued there and silently
      // captures B's colour instead of this chip's size. Looping -- following whichever
      // promise is current after each pass, capped at MAX_VARIANT_CHANGE_WAIT_PASSES -- closes
      // that gap.
      while (pendingVariantChange) {
        passes++;
        if (passes > MAX_VARIANT_CHANGE_WAIT_PASSES) {
          // A shopper would have to land a fresh colour click inside every single one of
          // MAX_VARIANT_CHANGE_WAIT_PASSES consecutive fetch windows to reach this. Treat it
          // the same as any other bail below rather than submit into a state this click can
          // no longer vouch for. We're abandoning this promise without awaiting it -- attach a
          // no-op rejection handler anyway (see the comment below) so a fetch that was still in
          // flight and later gets aborted doesn't surface as unhandled after we've walked away.
          pendingVariantChange.catch(() => {});
          bailed = true;
          break;
        }

        // Every pass waits on whatever colour-change promise is current, and every one of
        // those promises must tolerate rejection the same way: a second (or third, ...)
        // swatch click aborts the fetch behind the PREVIOUS promise and rejects it the
        // instant the newer one starts (see assets/variant-picker.js's fetchUpdatedSection),
        // so rejection here is routine, not exceptional -- it must never reach the console as
        // an unhandled rejection. try/catch around the await (rather than chaining
        // `.catch()`) makes that guarantee explicit for every iteration of this loop, not
        // just the first.
        try {
          await pendingVariantChange;
        } catch {
          // Rejected because a newer colour change superseded this one -- continue either
          // way and let the re-check below decide what happens next.
        }

        // The wait may have let a colour morph rewrite THIS SAME button node (see the class
        // doc comment and `#isCurrentClick` above: `childrenOnly: true` reuses chip nodes in
        // place), so `button` can now represent a different variant than the one this click
        // captured -- OR the morph may have removed the chip outright (the common case when
        // the new colour has fewer sizes than the old one) or, on a combined listing, replaced
        // the whole `<form>` wholesale (its id is keyed by product, so a swatch pointing at a
        // different connected product swaps it out from under this click). A detached node
        // keeps whatever attributes it had, so the `data-variant-id` comparison alone cannot
        // tell removal apart from a still-live match -- `button.isConnected` and
        // `form.isConnected` are checked alongside it for that reason. Any of these means this
        // click no longer owns what the chip currently means -- clear it and bail without
        // submitting rather than guessing.
        if (
          button.getAttribute('data-variant-id') !== variantId ||
          !button.isConnected ||
          !form.isConnected
        ) {
          bailed = true;
          break;
        }

        // Another colour change may have started while this pass was resolving. Follow
        // whichever promise is current now until it settles into null (nothing left in
        // flight) or into itself (nothing newer arrived while resolving this pass) -- see
        // `#onProductSelect` above for how a newer change supersedes the tracked promise.
        const next = this.#pendingVariantChange;
        if (next === null || next === pendingVariantChange) break;
        pendingVariantChange = next;
      }

      if (bailed) {
        this.#clearState(button);
        return;
      }

      // Still the same variant -- but the morph may have rewritten the hidden input to
      // whatever the new colour's own selection is, so restore it to what this click intended.
      variantInput.value = variantId;
    }

    let consumed = false;

    // product-form.js dispatches this on `pfc` itself -- synchronously, within the same fetch
    // `.then()` turn that later resolves this click's cartLinesUpdate promise -- only on the
    // error path, carrying the actual reason (e.g. "Tu carrito ya tiene la cantidad máxima de
    // este artículo"). That promise's own resolution value never carries the message (see
    // CartLinesUpdatePayload's `detail`), so this is the only way to recover it for the live
    // region announcement below instead of always falling back to a generic message.
    let errorMessage = /** @type {string | null} */ (null);
    const captureError = (/** @type {any} */ errorEvent) => {
      errorMessage = typeof errorEvent?.error === 'string' && errorEvent.error ? errorEvent.error : null;
    };

    const settle = (/** @type {any} */ event) => {
      consumed = true;

      const promise = event?.promise;
      if (!promise) {
        // No promise means we have no evidence the add actually happened -- never paint
        // success without proof. This branch runs synchronously within this same click's
        // turn (cartLinesUpdate dispatches synchronously inside requestSubmit, see
        // product-form.js), so the epoch can't have moved on yet -- no guard needed here. It
        // also runs before the fetch that would ever trigger a `cartError` event, so there's
        // nothing waiting to capture -- remove the listener now instead of leaking it.
        pfc.removeEventListener(StandardEvents.cartError, captureError);
        this.#markState(button, 'error');
        this.#announce(pfc, errorMessage || GENERIC_ADD_ERROR_MESSAGE);
        return;
      }

      promise
        .then(/** @param {any} result */ (result) => {
          // `cartError` (if this add errored) dispatches synchronously inside the same
          // fetch `.then()` that eventually resolves this promise, so by now it has already
          // fired and `errorMessage` (if set) reflects it -- safe to stop listening.
          pfc.removeEventListener(StandardEvents.cartError, captureError);

          const didError = result?.detail?.didError;
          // This request's own /cart/add.js round trip can outlast the pending watchdog
          // (~5s) and the shopper can legitimately re-click in the meantime. If that
          // happened, this chip has moved on to a newer epoch -- only the newer click owns
          // the chip's visual state now, so skip painting over it. The cart genuinely
          // received the add either way, so the cart-open side effect still fires.
          if (this.#isCurrentClick(button, epoch, variantId)) {
            this.#markState(button, didError ? 'error' : 'added');
            this.#announce(
              pfc,
              didError ? errorMessage || GENERIC_ADD_ERROR_MESSAGE : this.#addedAnnouncement(button)
            );
          }
          if (this.dataset.openCart === 'true' && !didError) this.#openCart();
        })
        .catch(() => {
          pfc.removeEventListener(StandardEvents.cartError, captureError);
          if (this.#isCurrentClick(button, epoch, variantId)) {
            this.#markState(button, 'error');
            this.#announce(pfc, errorMessage || GENERIC_ADD_ERROR_MESSAGE);
          }
        });
    };

    // Both listeners are registered here -- after the variant-change wait above has settled
    // and passed its re-check, immediately before requestSubmit() -- rather than earlier in
    // the handler. product-form.js dispatches cartLinesUpdate synchronously inside
    // requestSubmit(), on the shared `pfc` every chip on this card submits through. With two
    // chips clicked inside one colour-change window, registering before the wait would leave
    // BOTH chips' listeners attached on `pfc` while each awaits -- the first chip's dispatch
    // would then also fire the second chip's still-attached listeners, painting and announcing
    // the second chip from the first chip's cart result. Registering immediately before this
    // call closes that gap: there is no more `await` between registration and dispatch, so at
    // most one chip's listeners are ever attached at the moment a dispatch actually happens.
    //
    // Listening on THIS product-form-component (rather than document) scopes the listener to
    // this chip's own add: product-form-component dispatches cartLinesUpdate on itself
    // (`this.dispatchEvent(...)` inside ProductFormComponent), so a listener bound there only
    // ever observes events whose target IS this exact element -- a foreign card's add can
    // never reach it, bubbling or not (verified empirically: eventPhase === AT_TARGET,
    // event.target === pfc).
    //
    // The `consumed` flag guards the other half of the original assumption: that the dispatch
    // is synchronous. It usually is (ProductFormComponent#handleSubmit -> #processAddToCart
    // dispatches before returning), but handleSubmit returns early -- without dispatching
    // anything -- while a variant change is in flight (see assets/product-form.js, the
    // `#variantChangeInProgress` branch around L327-331). That branch USED TO be reachable from
    // here whenever a colour-swatch re-render put this same product-form-component into its
    // variant-change window while a size chip was clicked -- `!consumed` below would reset the
    // chip silently (no error shown) after the fact, once the submit had already been swallowed.
    // That race is now handled by the wait loop above: this click either waits out the change
    // (following whichever colour change supersedes the last, for up to
    // MAX_VARIANT_CHANGE_WAIT_PASSES passes) and re-verifies the chip before this point is ever
    // reached, or bails out before requestSubmit() is called at all. This flag stays in place
    // as the general safety net for any OTHER path that returns from requestSubmit() without
    // ever dispatching: recover immediately instead of leaving the chip pending (and disabled)
    // forever.
    pfc.addEventListener(StandardEvents.cartError, captureError, { once: true });
    pfc.addEventListener(StandardEvents.cartLinesUpdate, settle, { once: true });

    form.requestSubmit();

    if (!consumed) {
      pfc.removeEventListener(StandardEvents.cartLinesUpdate, settle);
      pfc.removeEventListener(StandardEvents.cartError, captureError);
      this.#clearState(button);
    }
  };

  /**
   * True when `button` still represents the click that's settling right now -- the epoch
   * check alone is not enough to prove that. A colour swatch change morphs this overlay
   * (see product-card.js's `updateSizeOverlay`), and because that `morph` runs with
   * `childrenOnly: true`, `<card-size-selector>` survives the swap and its chip `<button>`
   * nodes are reused in place: `data-variant-id`, `aria-label` and `disabled` get rewritten
   * on the SAME node rather than a new one being created. That reuse doesn't bump the
   * epoch -- only a click does -- so a chip can go from "Gris M" to "Azul S" mid-flight
   * while `#epochs.get(button)` still reads the value this click captured. Re-checking
   * `data-variant-id` against what was captured at click time closes that gap: if it no
   * longer matches, this response belongs to a variant the chip no longer represents.
   * @param {HTMLButtonElement} button
   * @param {number} epoch
   * @param {string} variantId
   * @returns {boolean}
   */
  #isCurrentClick(button, epoch, variantId) {
    return this.#epochs.get(button) === epoch && button.getAttribute('data-variant-id') === variantId;
  }

  /**
   * Builds the Spanish success announcement, naming the size that was just added.
   * @param {HTMLButtonElement} button
   * @returns {string}
   */
  #addedAnnouncement(button) {
    const size = button.querySelector('.card-sizes__item-label')?.textContent?.trim();
    return size ? `Talla ${size} agregada al carrito.` : 'Talla agregada al carrito.';
  }

  /**
   * Writes a message into the card's existing live region so screen reader users hear the
   * outcome of a click. The node (`ref="liveRegion"`, `aria-live="assertive"`, `role="status"`)
   * is rendered by snippets/product-card-sizes-markup.liquid and is one of
   * ProductFormComponent's own `requiredRefs`, so it's guaranteed to exist once `pfc` has
   * connected. Nothing upstream ever populates it for this card, though: assets/product-form.js's
   * own announcement calls are gated on refs (`allAddToCartContainers[0]?.refs.addToCartButton`)
   * that this card's minimal
   * form never renders, so they silently no-op here. This fills the gap without touching that
   * file.
   *
   * Schedules a clear of the text after `ANNOUNCEMENT_CLEAR_MS`, cancelling any previously
   * scheduled clear first. `aria-live` only announces on a text change, so writing the same
   * message twice in a row (e.g. adding the same size back-to-back) would otherwise announce
   * nothing the second time -- see product-form.js's `#setLiveRegionText` / `#clearLiveRegionText`
   * for the same pairing.
   * @param {Element} pfc
   * @param {string} message
   */
  #announce(pfc, message) {
    const liveRegion = /** @type {any} */ (pfc).refs?.liveRegion;
    if (!(liveRegion instanceof HTMLElement)) return;

    if (this.#announceClearTimer !== null) clearTimeout(this.#announceClearTimer);

    liveRegion.textContent = message;
    this.#announceClearTimer = setTimeout(() => {
      this.#announceClearTimer = null;
      liveRegion.textContent = '';
    }, ANNOUNCEMENT_CLEAR_MS);
  }

  /**
   * Applies a transient visual state to the clicked chip. `pending` is watched by a bounded
   * watchdog so an add that never resolves can't brick the chip; `added` / `error` clear
   * themselves after a short confirmation window.
   * @param {HTMLButtonElement} button
   * @param {'pending' | 'added' | 'error'} state
   */
  #markState(button, state) {
    const previous = this.#resetTimers.get(button);
    if (previous) clearTimeout(previous.timer);

    // Remember whatever `aria-disabled` value the chip had before this state cycle started
    // (e.g. sold-out chips render `aria-disabled="true"` statically in the markup, alongside a
    // real `disabled` attribute this feature never touches) so clearing the state restores
    // that instead of clobbering it. `pending` is communicated via `aria-disabled` rather than
    // the `disabled` property specifically so the click doesn't blur the chip: a keyboard
    // shopper's Enter press would otherwise move focus to <body> and, since the overlay is only
    // revealed by hover/`:focus-within`, hide the very confirmation the click was for. Re-entry
    // while pending is guarded separately in #handleClick via `data-state`.
    const wasAriaDisabled = previous ? previous.wasAriaDisabled : button.getAttribute('aria-disabled');

    button.dataset.state = state;
    if (state === 'pending') {
      button.setAttribute('aria-disabled', 'true');
    } else if (wasAriaDisabled === null) {
      button.removeAttribute('aria-disabled');
    } else {
      button.setAttribute('aria-disabled', wasAriaDisabled);
    }

    const delay = state === 'pending' ? PENDING_WATCHDOG_MS : RESOLVED_STATE_RESET_MS;

    // Capture this cycle's epoch so the reset below can recognize itself as superseded. In
    // practice a newer #markState call for this button already clearTimeout'd this timer
    // before it could fire (every call above clears `previous.timer`) -- this check is
    // belt-and-suspenders against that invariant ever breaking in a future refactor.
    const stateEpoch = this.#epochs.get(button);

    const timer = setTimeout(() => {
      // Not just defensive: a colour swatch change re-renders this overlay and `morph`
      // (childrenOnly: true) removes surplus chip nodes when the new colour has fewer sizes
      // than the old one (see product-card.js's `updateSizeOverlay`), so a chip whose reset
      // timer is still pending can genuinely be disconnected by the time this fires --
      // `!button.isConnected` below is exercised in normal use, not hypothetical.
      this.#resetTimers.delete(button);
      if (this.#epochs.get(button) !== stateEpoch) return;
      if (!button.isConnected) return;
      button.removeAttribute('data-state');
      if (wasAriaDisabled === null) {
        button.removeAttribute('aria-disabled');
      } else {
        button.setAttribute('aria-disabled', wasAriaDisabled);
      }
    }, delay);

    this.#resetTimers.set(button, { timer, wasAriaDisabled });
  }

  /**
   * Synchronously cancels any in-flight state/timer for a chip and restores it to rest, as if
   * `pending` had never been set. Used when a submit resolves without ever dispatching
   * cartLinesUpdate.
   * @param {HTMLButtonElement} button
   */
  #clearState(button) {
    const entry = this.#resetTimers.get(button);
    if (entry) clearTimeout(entry.timer);
    this.#resetTimers.delete(button);

    button.removeAttribute('data-state');
    if (!entry || entry.wasAriaDisabled === null) {
      button.removeAttribute('aria-disabled');
    } else {
      button.setAttribute('aria-disabled', entry.wasAriaDisabled);
    }
  }

  /** Opens the cart drawer if it isn't already open. */
  #openCart() {
    const drawer = document.getElementById('cart-drawer');
    if (drawer && typeof (/** @type {any} */ (drawer).open) === 'function' && !(/** @type {any} */ (drawer).isOpen)) {
      /** @type {any} */ (drawer).open();
    }
  }
}

if (!customElements.get('card-size-selector')) {
  customElements.define('card-size-selector', CardSizeSelector);
}
